/* In-memory mocked clients for Task Management — no real backend exists for this domain yet.
 * NOTE: legacy v1 task endpoints (/tasks, /automatedtask/*) are separate (generated.ts, real backend). */

import {
  IOperationalDaysClient,
  IOperationalOverviewClient,
  ITasksClient,
  ITaskTemplatesClient,
  ITaskCategoriesClient,
  ITaskStatusesClient,
  ITaskPrioritiesClient,
  ITaskSchedulesClient,
  ITaskReportingDashboardClient,
} from './ITasksClient';
import {
  OperationalDayTemplateDto,
  CreateUpdateOperationalDayTemplateRequest,
  TaskTemplateDto,
  TaskCategoryDto,
  TaskPriorityDto,
  TaskStatusDto,
  TaskScheduleDto,
  TaskDashboardResponse,
  OperationalOverviewResponse,
  TaskSummaryDto,
  TaskDetailDto,
  TaskEvidenceDto,
  TaskEventDto,
  SuccessResponse,
  BulkReassignRequest,
  BulkPriorityUpdateRequest,
  CreateTaskRequest,
  ReassignTaskRequest,
  BulkReassignTasksRequest,
  ChangePriorityRequest,
  BulkChangePriorityRequest,
} from './tasksTypes';
import { tasksMockStore } from '../../../../mocks/data/tasksData';

const success = <T,>(data: T): SuccessResponse<T> => ({ data });

const isCompletedStatus = (statusId: number): boolean =>
  tasksMockStore.taskStatuses.some((s) => s.id === statusId && s.name === 'Completed');

const isOverdue = (task: TaskDetailDto): boolean =>
  !!task.dueDateUtc && new Date(task.dueDateUtc) < new Date() && !isCompletedStatus(task.statusId);

/**
 * The seed data repeats the same status/priority/category NAMES across several franchise-scoped
 * id ranges (e.g. "Completed" exists as id 6, 16, and 26). Demo data ignores franchise scoping
 * entirely, so callers must dedupe by name to avoid showing the same status/priority 2-3x over.
 */
const dedupeByName = <T extends { name: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.name.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const mapToSummary = (task: TaskDetailDto): TaskSummaryDto => ({
  id: task.id,
  title: task.title,
  storeNumber: task.storeNumber,
  storeName: task.storeName,
  categoryName: tasksMockStore.taskCategories.find((c) => c.id === task.categoryId)?.name ?? 'Uncategorized',
  assignedUserId: task.targetUserId,
  assignedUserName: task.targetUserName ?? undefined,
  taskSourceType: task.taskSourceType,
  statusId: task.statusId,
  priorityId: task.priorityId,
  defaultPriorityId: task.defaultPriorityId,
  startDateUtc: task.startDateUtc,
  dueDateUtc: task.dueDateUtc,
  completedDateUtc: task.completedDateUtc,
  isOverdue: isOverdue(task),
  percentageComplete: task.percentageComplete,
});

const buildDashboard = (): TaskDashboardResponse => {
  const tasks = tasksMockStore.tasks;

  // Group by NAME (not id) and sum counts across every franchise-scoped id that shares that
  // name, since demo data spans all franchises regardless of which store/franchise is active.
  const statusMetrics = dedupeByName(tasksMockStore.taskStatuses)
    .map((s) => {
      const matchingIds = tasksMockStore.taskStatuses.filter((x) => x.name === s.name).map((x) => x.id);
      return { statusId: s.id, statusName: s.name, count: tasks.filter((t) => matchingIds.includes(t.statusId)).length };
    })
    .filter((m) => m.count > 0);

  const priorityMetrics = dedupeByName(tasksMockStore.taskPriorities)
    .map((p) => {
      const matchingIds = tasksMockStore.taskPriorities.filter((x) => x.name === p.name).map((x) => x.id);
      return { priorityId: p.id, priorityName: p.name, count: tasks.filter((t) => matchingIds.includes(t.priorityId)).length };
    })
    .filter((m) => m.count > 0);

  const overdueByStore: Record<string, number> = {};
  tasks.filter(isOverdue).forEach((t) => {
    const key = t.storeName || t.storeNumber;
    overdueByStore[key] = (overdueByStore[key] ?? 0) + 1;
  });
  const overdueMetrics = Object.entries(overdueByStore).map(([storeOrFranchise, overdueCount]) => ({ storeOrFranchise, overdueCount }));

  const completionTrend = Array.from({ length: 7 }).map((_, i) => {
    const day = new Date();
    day.setDate(day.getDate() - (6 - i));
    const dayStr = day.toISOString().slice(0, 10);
    return {
      day: dayStr,
      createdCount: tasks.filter((t) => t.startDateUtc?.slice(0, 10) === dayStr || t.dueDateUtc?.slice(0, 10) === dayStr).length,
      completedCount: tasks.filter((t) => t.completedDateUtc?.slice(0, 10) === dayStr).length,
    };
  });

  const completedTasks = tasks.filter((t) => isCompletedStatus(t.statusId)).length;
  const overdueTasks = tasks.filter(isOverdue).length;

  return {
    statusMetrics,
    overdueMetrics,
    completionTrend,
    priorityMetrics,
    totalTasks: tasks.length,
    activeTasks: tasks.length - completedTasks,
    completedTasks,
    overdueTasks,
  };
};

export class OperationalDaysClientMocked implements IOperationalDaysClient {
  async getOperationalDays(isActive?: boolean, templateType?: string): Promise<SuccessResponse<OperationalDayTemplateDto[]>> {
    let days = tasksMockStore.operationalDays;
    if (isActive !== undefined) days = days.filter((d) => d.isActive === isActive);
    if (templateType) days = days.filter((d) => d.templateType === templateType);
    return success(days);
  }

  async getOperationalDayById(id: number): Promise<SuccessResponse<OperationalDayTemplateDto>> {
    const day = tasksMockStore.operationalDays.find((d) => d.id === id);
    if (!day) throw new Error(`Operational day ${id} not found`);
    return success(day);
  }

  async createOperationalDay(req: CreateUpdateOperationalDayTemplateRequest): Promise<SuccessResponse<OperationalDayTemplateDto>> {
    const newDay: OperationalDayTemplateDto = {
      id: tasksMockStore.nextId(),
      name: req.name ?? 'Operational Day',
      description: req.description ?? null,
      templateType: req.templateType ?? null,
      effectiveFromDate: req.effectiveFromDate ?? new Date().toISOString(),
      effectiveToDate: req.effectiveToDate ?? null,
      isActive: req.isActive ?? true,
      cronExpression: req.cronExpression ?? null,
    };
    tasksMockStore.operationalDays.push(newDay);
    return success(newDay);
  }

  async updateOperationalDay(id: number, req: CreateUpdateOperationalDayTemplateRequest): Promise<void> {
    const idx = tasksMockStore.operationalDays.findIndex((d) => d.id === id);
    if (idx !== -1) tasksMockStore.operationalDays[idx] = { ...tasksMockStore.operationalDays[idx], ...req };
  }

  async deleteOperationalDay(id: number): Promise<void> {
    const idx = tasksMockStore.operationalDays.findIndex((d) => d.id === id);
    if (idx !== -1) tasksMockStore.operationalDays.splice(idx, 1);
  }
}

export class OperationalOverviewClientMocked implements IOperationalOverviewClient {
  async getOverview(storeNumber: string, _options?: {
    day?: string | Date;
    statusIds?: number[];
    categoryIds?: number[];
    priorityIds?: number[];
    userIds?: number[];
    includeScheduled?: boolean;
    includeOperationalDay?: boolean;
    includeAdhoc?: boolean;
  }): Promise<SuccessResponse<OperationalOverviewResponse>> {
    // Demo data ignores store/franchise scoping entirely - use all tasks regardless of storeNumber.
    const storeTasks = tasksMockStore.tasks;
    return success({
      storeNumber,
      day: new Date().toISOString(),
      lanes: [],
      totalTasks: storeTasks.length,
      completedTasks: storeTasks.filter((t) => isCompletedStatus(t.statusId)).length,
      overdueTasks: storeTasks.filter(isOverdue).length,
    });
  }

  async bulkReassign(_req: BulkReassignRequest): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async bulkPriority(_req: BulkPriorityUpdateRequest): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }
}

export class TasksClientMocked implements ITasksClient {
  async getStoreTasks(_storeNumber: string, _franchiseName: string, fromDueUtc?: string | Date, toDueUtc?: string | Date): Promise<TaskSummaryDto[]> {
    // Demo data ignores store/franchise scoping entirely - return all tasks regardless of store.
    let tasks = tasksMockStore.tasks;
    if (fromDueUtc) {
      const from = new Date(fromDueUtc).getTime();
      tasks = tasks.filter((t) => !t.dueDateUtc || new Date(t.dueDateUtc).getTime() >= from);
    }
    if (toDueUtc) {
      const to = new Date(toDueUtc).getTime();
      tasks = tasks.filter((t) => !t.dueDateUtc || new Date(t.dueDateUtc).getTime() <= to);
    }
    return tasks.map(mapToSummary);
  }

  async getTask(taskId: number): Promise<TaskDetailDto> {
    const task = tasksMockStore.tasks.find((t) => t.id === taskId);
    if (!task) throw new Error(`Task ${taskId} not found`);
    return task;
  }

  async getEvidence(taskId: number): Promise<TaskEvidenceDto[]> {
    const task = tasksMockStore.tasks.find((t) => t.id === taskId);
    return task?.evidence ?? [];
  }

  async getAudit(taskId: number): Promise<TaskEventDto[]> {
    const task = tasksMockStore.tasks.find((t) => t.id === taskId);
    return task?.history ?? [];
  }

  async createTask(req: CreateTaskRequest): Promise<number> {
    const newId = tasksMockStore.nextId();
    const now = new Date().toISOString();
    const fallbackCategoryId = tasksMockStore.taskCategories[0]?.id ?? 1;
    const newTask: TaskDetailDto = {
      id: newId,
      templateId: req.taskTemplateId ?? null,
      title: req.title,
      description: req.description ?? '',
      storeNumber: req.storeNumber,
      storeName: req.storeName ?? req.storeNumber,
      statusId: 1,
      priorityId: req.taskPriorityId,
      defaultPriorityId: req.taskPriorityId,
      categoryId: fallbackCategoryId,
      percentageComplete: 0,
      dueDateUtc: req.dueDateTimeUtc ?? null,
      startDateUtc: null,
      completedDateUtc: null,
      templateName: req.title,
      targetUserId: req.targetUserId,
      targetUserName: req.targetUserName ?? null,
      targetRoleId: req.targetRoleId,
      targetRoleName: req.targetRoleName ?? null,
      steps: [],
      evidence: [],
      history: [
        { id: tasksMockStore.nextId(), eventType: 'Created', oldValue: null, newValue: null, comment: 'Task created via API', createdAtUtc: now, createdByUserId: null },
      ],
      requiresApproval: false,
      approverUserId: null,
      approverPersonaId: null,
      approvalResult: null,
      approvalNotes: null,
      approvalDateUtc: null,
      approvalByUserId: null,
      taskSourceType: 'AdHoc',
    };
    tasksMockStore.tasks.push(newTask);
    return newId;
  }

  async createTasksBulk(reqs: CreateTaskRequest[]): Promise<number[]> {
    return Promise.all(reqs.map((req) => this.createTask(req)));
  }

  async reassignTask(_taskId: number, _req: ReassignTaskRequest): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async bulkReassign(_req: BulkReassignTasksRequest): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async changePriority(_taskId: number, _req: ChangePriorityRequest): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async bulkChangePriority(_req: BulkChangePriorityRequest): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async deleteTask(taskId: number): Promise<void> {
    const idx = tasksMockStore.tasks.findIndex((t) => t.id === taskId);
    if (idx !== -1) tasksMockStore.tasks.splice(idx, 1);
  }
}

export class TaskTemplatesClientMocked implements ITaskTemplatesClient {
  async getTemplates(): Promise<TaskTemplateDto[]> {
    return tasksMockStore.taskTemplates;
  }

  async getTemplate(id: number): Promise<TaskTemplateDto> {
    const tpl = tasksMockStore.taskTemplates.find((t) => t.id === id);
    if (!tpl) throw new Error(`Task template ${id} not found`);
    return tpl;
  }

  async createTemplate(dto: TaskTemplateDto): Promise<void> {
    tasksMockStore.taskTemplates.push({ ...dto, id: dto.id ?? tasksMockStore.nextId() });
  }

  async updateTemplate(id: number, dto: TaskTemplateDto): Promise<void> {
    const idx = tasksMockStore.taskTemplates.findIndex((t) => t.id === id);
    if (idx !== -1) tasksMockStore.taskTemplates[idx] = { ...dto, id };
  }

  async deleteTemplate(id: number): Promise<void> {
    const idx = tasksMockStore.taskTemplates.findIndex((t) => t.id === id);
    if (idx !== -1) tasksMockStore.taskTemplates.splice(idx, 1);
  }
}

export class TaskCategoriesClientMocked implements ITaskCategoriesClient {
  async getCategories(): Promise<TaskCategoryDto[]> {
    return tasksMockStore.taskCategories;
  }

  async createCategory(dto: TaskCategoryDto): Promise<void> {
    tasksMockStore.taskCategories.push({ ...dto, id: dto.id ?? tasksMockStore.nextId() });
  }

  async updateCategory(id: number, dto: TaskCategoryDto): Promise<void> {
    const idx = tasksMockStore.taskCategories.findIndex((c) => c.id === id);
    if (idx !== -1) tasksMockStore.taskCategories[idx] = { ...dto, id };
  }

  async deleteCategory(id: number): Promise<void> {
    const idx = tasksMockStore.taskCategories.findIndex((c) => c.id === id);
    if (idx !== -1) tasksMockStore.taskCategories.splice(idx, 1);
  }
}

export class TaskStatusesClientMocked implements ITaskStatusesClient {
  async getStatuses(): Promise<TaskStatusDto[]> {
    return dedupeByName(tasksMockStore.taskStatuses);
  }
}

export class TaskPrioritiesClientMocked implements ITaskPrioritiesClient {
  async getPriorities(): Promise<TaskPriorityDto[]> {
    return dedupeByName(tasksMockStore.taskPriorities);
  }
}

export class TaskSchedulesClientMocked implements ITaskSchedulesClient {
  async getSchedules(): Promise<TaskScheduleDto[]> {
    return tasksMockStore.taskSchedules;
  }

  async getSchedule(id: number): Promise<TaskScheduleDto> {
    const schedule = tasksMockStore.taskSchedules.find((s) => s.id === id);
    if (!schedule) throw new Error(`Task schedule ${id} not found`);
    return schedule;
  }

  async createSchedule(dto: TaskScheduleDto): Promise<void> {
    tasksMockStore.taskSchedules.push({ ...dto, id: dto.id ?? tasksMockStore.nextId() });
  }

  async updateSchedule(id: number, dto: TaskScheduleDto): Promise<void> {
    const idx = tasksMockStore.taskSchedules.findIndex((s) => s.id === id);
    if (idx !== -1) tasksMockStore.taskSchedules[idx] = { ...dto, id };
  }

  async deleteSchedule(id: number): Promise<void> {
    const idx = tasksMockStore.taskSchedules.findIndex((s) => s.id === id);
    if (idx !== -1) tasksMockStore.taskSchedules.splice(idx, 1);
  }
}

export class TaskReportingDashboardClientMocked implements ITaskReportingDashboardClient {
  async getDashboard(): Promise<SuccessResponse<TaskDashboardResponse>> {
    return success(buildDashboard());
  }

  async getReportingTasks(): Promise<TaskSummaryDto[]> {
    return tasksMockStore.tasks.map(mapToSummary);
  }
}
